import { useNavigate } from 'react-router-dom';
import {
  HiOutlineBuildingOffice,
  HiOutlineFolderOpen,
  HiOutlineHomeModern,
  HiOutlineCurrencyDollar,
} from 'react-icons/hi2';
import type { OrgStats } from '../../api/analytics';
import { StatCard } from '../shared/StatCard';
import { ChartCard } from '../charts/ChartCard';
import { CostDonutChart } from '../charts/CostDonutChart';
import { ComparisonBarChart } from '../charts/ComparisonBarChart';
import { DataTable } from '../shared/DataTable';
import type { Column } from '../shared/DataTable';
import { buildCostDonutData } from '../charts/chartTheme';
import { fmt, fmtCHF, fmtM } from '../../utils/format';
import styles from './OrgAnalyticsPage.module.css';

interface Props {
  data: OrgStats;
}

export function FederalView({ data }: Props) {
  const navigate = useNavigate();

  const donutData = buildCostDonutData(data.cost_breakdown);

  const barData = data.children_stats.map((c) => ({
    name: c.org_name.length > 15 ? c.org_name.slice(0, 15) + '...' : c.org_name,
    logements: c.total_logements,
    emplois: c.total_emplois,
  }));

  const columns: Column<typeof data.children_stats[number]>[] = [
    { key: 'org_name', label: 'Canton' },
    { key: 'project_count', label: 'Projects', align: 'right', render: (r) => fmt(r.project_count) },
    { key: 'total_logements', label: 'Logements', align: 'right', render: (r) => fmt(r.total_logements) },
    { key: 'total_emplois', label: 'Emplois', align: 'right', render: (r) => fmt(r.total_emplois) },
    { key: 'total_ha', label: 'Ha', align: 'right', render: (r) => r.total_ha > 0 ? r.total_ha.toFixed(1) : '\u2014' },
    {
      key: 'density',
      label: 'Density',
      align: 'right',
      render: (r) => r.total_ha > 0 ? (r.total_logements / r.total_ha).toFixed(1) : '\u2014',
    },
  ];

  return (
    <>
      <div className={styles.statsRow}>
        <StatCard label="Cantons" value={data.children_stats.length} icon={<HiOutlineBuildingOffice />} />
        <StatCard label="Total Projects" value={data.total_projects} icon={<HiOutlineFolderOpen />} />
        <StatCard label="Total Logements" value={fmt(data.total_logements)} icon={<HiOutlineHomeModern />} />
        <StatCard
          label="Total Investment"
          value={data.total_cost > 0 ? fmtCHF(data.total_cost) : '\u2014'}
          icon={<HiOutlineCurrencyDollar />}
          color="var(--color-primary)"
        />
      </div>

      <div className={styles.chartsRow}>
        <ChartCard title="Projects by Canton" subtitle="Logements & Emplois">
          <ComparisonBarChart
            data={barData}
            metrics={[
              { key: 'logements', label: 'Logements', color: '#6366f1' },
              { key: 'emplois', label: 'Emplois', color: '#22d3ee' },
            ]}
            layout="horizontal"
          />
        </ChartCard>
        <ChartCard title="Cost Breakdown" subtitle="All projects">
          <CostDonutChart
            data={donutData}
            centerValue={data.total_cost > 0 ? fmtM(data.total_cost) : undefined}
            centerLabel="Total"
          />
        </ChartCard>
      </div>

      <div className={styles.tableSection}>
        <h3>Cantons Overview</h3>
        <DataTable
          columns={columns}
          data={data.children_stats}
          keyField="org_id"
          onRowClick={(child) => navigate(`/app/analytics/${child.org_id}`)}
        />
      </div>
    </>
  );
}
